import { FormEvent, KeyboardEvent, useState } from 'react'

import { DistributionChannel } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { TextArea } from '@avantstay/backoffice-core'

import { GUEST_INBOX_V2_EMAILS } from '@new/__global/constants/emailsList'
import { useEmailPermission } from '@new/__global/hooks/useEmailPermission'

import { SPOKEPHONE_LABEL } from '../../../../StayTimeline.consts'
import { MessageDestination } from '../../../../StayTimeline.types'
import { getOtaReplyLabel } from '../../../../StayTimeline.utils'
import * as Shared from '../../MessageComposer.styles'
import { isSubmitShortcut } from '../../MessageComposer.utils'
import * as S from './ReplyComposer.styles'

interface ReplyComposerProps {
  guestName: string
  otaChannel?: DistributionChannel
  draft: string
  isSending: boolean
  placeholder?: string
  submitLabel?: string
  defaultDestination?: MessageDestination
  onDraftChange: (draft: string) => void
  onSend: (message: string, destination: MessageDestination) => Promise<boolean>
}

export const ReplyComposer = ({
  guestName,
  otaChannel,
  draft,
  isSending,
  placeholder,
  submitLabel = 'Send',
  defaultDestination = 'ota',
  onDraftChange,
  onSend,
}: ReplyComposerProps) => {
  const [selectedDestination, setSelectedDestination] = useState<MessageDestination>()

  const canReplyThroughOta = useEmailPermission({ allowedEmails: GUEST_INBOX_V2_EMAILS })

  const otaLabel = canReplyThroughOta ? getOtaReplyLabel(otaChannel) : undefined
  const destination = otaLabel ? (selectedDestination ?? defaultDestination) : 'spokephone'
  const alternativeDestination = destination === 'ota' ? 'spokephone' : 'ota'
  const canSend = !!draft.trim() && !isSending

  const getDestinationLabel = (target: MessageDestination) =>
    target === 'ota' ? otaLabel : SPOKEPHONE_LABEL

  const send = async (target = destination) => {
    if (!canSend) return

    const success = await onSend(draft, target)
    if (success) onDraftChange('')
  }

  const sendThroughAlternative = () => {
    setSelectedDestination(alternativeDestination)
    void send(alternativeDestination)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (!isSubmitShortcut(event)) return

    event.preventDefault()
    void send()
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void send()
  }

  return (
    <form onKeyDown={handleKeyDown} onSubmit={handleSubmit}>
      <TextArea
        rows={3}
        value={draft}
        disabled={isSending}
        placeholder={placeholder ?? `Reply to ${guestName}…`}
        onChange={event => onDraftChange(event.target.value)}
      />

      <Shared.Actions>
        <Shared.Details>
          <Shared.Hint>
            Sent to the guest{' '}
            {destination === 'ota' ? `through ${otaLabel}` : `over ${SPOKEPHONE_LABEL}`}. Messages
            cannot be edited or recalled once sent.
          </Shared.Hint>
        </Shared.Details>

        {otaLabel ? (
          <S.SendButton
            disabled={!canSend}
            loading={isSending}
            actions={[
              {
                label: `${submitLabel} via ${getDestinationLabel(destination)}`,
                action: () => void send(),
              },
              {
                label: (
                  <>
                    <strong>{submitLabel}</strong> via {getDestinationLabel(alternativeDestination)}
                  </>
                ),
                action: sendThroughAlternative,
              },
            ]}
          />
        ) : (
          <Shared.SubmitButton type="submit" disabled={!canSend} loading={isSending}>
            {submitLabel}
          </Shared.SubmitButton>
        )}
      </Shared.Actions>
    </form>
  )
}
